import { useState } from 'react'
import { useMatch, useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, ChevronDown, ArchiveRestore, Archive } from 'lucide-react'
import { usePersonalSpaces } from '../../lib/personal-spaces'
import { api, extractApiError } from '../../lib/api'
import { SpaceTreeRow } from './AdminCurrentSpaceTree'
import { SpaceCreateDialog } from '../SpaceCreateDialog'
import { SpaceShareDialog } from '../SpaceShareDialog'

/**
 * The admin's PERSONAL, private Spaces in the sidebar — the same inline tree the
 * User shell renders (a Space expandable to Projects → Lists with the full row
 * actions), but scoped to the admin's own personal space(s), NOT the shared
 * department Spaces. Department / company Spaces live under "Space Explorer";
 * this section is the admin's private working area.
 *
 * Reuses the exported `SpaceTreeRow` so a personal Space behaves identically to
 * any other Space. Renders a placeholder until the personal space exists (it is
 * auto-provisioned server-side).
 */
export function AdminSpacesTree({ collapsed = false }) {
  const navigate = useNavigate()
  const spaceMatch = useMatch('/app/spaces/:spaceId/*')
  const listMatch = useMatch('/app/spaces/:spaceId/lists/:listId')
  const activeSpaceId = spaceMatch?.params?.spaceId ?? null
  const activeListId = listMatch?.params?.listId ?? null

  const qc = useQueryClient()

  // Accordion — one personal Space open at a time. `undefined` means
  // "untouched": follow the active Space so entering one auto-expands it.
  const [openId, setOpenId] = useState(undefined)
  const [shareTarget, setShareTarget] = useState(null)
  const [createOpen, setCreateOpen] = useState(false)
  // Collapsed by default — the archived list is a recovery affordance, not
  // part of the everyday tree.
  const [archivedOpen, setArchivedOpen] = useState(false)

  // ONLY the admin's own personal, private Space(s) — department / shared
  // Spaces belong to Space Explorer. Shared hook + cache (see personal-spaces).
  const {
    workspaceId,
    personalSpaces: spaces,
    archivedPersonalSpaces: archived,
    isLoading,
  } = usePersonalSpaces()

  // Un-archive a personal Space. This is an UPDATE (not an insert), so it never
  // touches the owner-uniqueness index — a personal Space can always be
  // restored even if the legacy one-per-user index is still present.
  const restoreMutation = useMutation({
    mutationFn: async (sp) => {
      await api.patch(`/workspaces/${workspaceId}/spaces/${sp.id}`, {
        isArchived: false,
      })
      return sp
    },
    onSuccess: (sp) => {
      toast.success('Space restored successfully.')
      void qc.invalidateQueries({ queryKey: ['spaces', workspaceId] })
      void qc.invalidateQueries({ queryKey: ['space', sp.id] })
      navigate(`/app/spaces/${sp.id}`)
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  // Deep nesting is meaningless at icon width — the collapsed rail keeps just
  // the section icon above (handled by the layout).
  if (collapsed || !workspaceId) return null

  const expandedId = openId === undefined ? activeSpaceId : openId

  return (
    <div className="mt-1 ml-3 pl-2 border-l border-white/10 space-y-0.5">
      {isLoading ? (
        <p className="px-3 py-1 text-[length:calc(var(--font-size-scale,1)*11px)] italic text-white/30">Loading…</p>
      ) : spaces.length === 0 ? (
        <p className="px-3 py-1 text-[length:calc(var(--font-size-scale,1)*11px)] italic text-white/30">
          No personal spaces yet.
        </p>
      ) : (
        spaces.map((sp) => (
          <SpaceTreeRow
            key={sp.id}
            space={sp}
            workspaceId={workspaceId}
            isActive={sp.id === activeSpaceId}
            isExpanded={sp.id === expandedId}
            onToggle={() => setOpenId(expandedId === sp.id ? null : sp.id)}
            activeListId={activeListId}
            onShare={() => setShareTarget(sp)}
          />
        ))
      )}

      {/* + New Space — creates a PERSONAL Space (private, owner-only), like
       *  the User shell's Spaces tree. */}
      <button
        type="button"
        onClick={() => setCreateOpen(true)}
        className="w-full flex items-center gap-2.5 rounded-[10px] px-3 py-1.5 text-xs text-left cursor-pointer text-white/55 hover:text-white hover:bg-white/5 transition-colors"
      >
        <Plus size={13} strokeWidth={1.75} className="shrink-0" />
        New Space
      </button>

      {/* Archived personal Spaces — a collapsible restore list. Only the
       *  owner's archived personal Spaces reach here (see usePersonalSpaces);
       *  Restore un-archives so the Space returns to the active list above.
       *  Without this an archived personal Space is unrecoverable from the UI. */}
      {archived.length > 0 ? (
        <div className="mt-1">
          <button
            type="button"
            onClick={() => setArchivedOpen((v) => !v)}
            aria-expanded={archivedOpen}
            className="w-full flex items-center gap-2.5 rounded-[10px] px-3 py-1.5 text-xs text-left cursor-pointer text-white/45 hover:text-white/80 hover:bg-white/5 transition-colors"
          >
            <ChevronDown
              size={13}
              strokeWidth={1.75}
              className={
                'shrink-0 transition-transform duration-150 ' +
                (archivedOpen ? '' : '-rotate-90')
              }
            />
            <span className="flex-1 truncate">Archived ({archived.length})</span>
          </button>
          {archivedOpen ? (
            <div className="mt-0.5 ml-3 pl-2 border-l border-white/10 space-y-0.5">
              {archived.map((sp) => (
                <div
                  key={sp.id}
                  className="flex items-center gap-2 rounded-[10px] px-3 py-1.5 text-xs text-white/55"
                >
                  <Archive
                    size={13}
                    strokeWidth={1.75}
                    className="shrink-0 text-white/35"
                  />
                  <span className="flex-1 truncate" title={sp.name}>
                    {sp.name}
                  </span>
                  <button
                    type="button"
                    onClick={() => restoreMutation.mutate(sp)}
                    disabled={restoreMutation.isPending}
                    title={`Restore "${sp.name}"`}
                    aria-label={`Restore ${sp.name}`}
                    className="shrink-0 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-white/60 hover:text-white hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
                  >
                    <ArchiveRestore size={12} strokeWidth={1.75} />
                    Restore
                  </button>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      <SpaceCreateDialog
        open={createOpen}
        workspaceId={workspaceId}
        personal
        onClose={() => setCreateOpen(false)}
        onCreated={(spaceId) => navigate(`/app/spaces/${spaceId}`)}
      />
      <SpaceShareDialog
        open={Boolean(shareTarget)}
        spaceId={shareTarget?.id ?? null}
        workspaceId={workspaceId}
        spaceName={shareTarget?.name ?? ''}
        isPersonal={shareTarget?.isPersonal === true}
        onClose={() => setShareTarget(null)}
      />
    </div>
  )
}
